import {
  FC,
  JSX,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { DesignerSetting } from '@app/DesignerSetting';
import {
  LoadingSpinner,
  Message,
  MessageStatus
} from '@app/form_components';
import { useServices } from '@app/hooks';
import { AvsChangeLogs } from '@app/types';
import { extractJiraTickets } from '@app/utilities';
import styles from './MergeChangesSummary.module.scss';

export type MergeChangesSummaryProps = {
  /** Branch id of the source branch whose change logs should be summarized. */
  sourceBranchId: string;
  /** Version of the source branch from which to load change logs. */
  sourceBranchVersion: string;
  /**
   * Called once after the AI summary is successfully generated. The merge
   * dialog uses this to append the summary into its commit-message textarea.
   */
  onSummaryGenerated?: (summary: string) => void;
};

/**
 * Displays an AI-generated summary of every save message on the source branch
 * being merged in, plus a deduped list of any Jira tickets referenced by those
 * messages, plus a collapsible list of the underlying commit messages with
 * their authors. Rendered at the bottom of the "Your changes" tab in the merge
 * dialog.
 */
export const MergeChangesSummary: FC<MergeChangesSummaryProps> = ({
  sourceBranchId,
  sourceBranchVersion,
  onSummaryGenerated
}) => {
  const { avsService, geminiService } = useServices();
  const abortControllerRef = useRef<AbortController | null>(null);
  const [entries, setEntries] = useState<AvsChangeLogs[]>([]);
  const [summary, setSummary] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Latest callback, read inside the effect without re-firing it on prop change.
  const onSummaryGeneratedRef = useRef(onSummaryGenerated);
  onSummaryGeneratedRef.current = onSummaryGenerated;

  const loadSummary = useCallback(async () => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading(true);
    setError(null);
    setSummary('');
    setEntries([]);

    try {
      const loaded = (await avsService.getChangeLogs(
        sourceBranchId,
        sourceBranchVersion,
        { count: DesignerSetting.s.maxSaveMessagesToSummarize }
      )).sort((a, b) => a.timeStamp - b.timeStamp);
      if (controller.signal.aborted) {
        return;
      }

      setEntries(loaded);
      if (!loaded.length) {
        return;
      }

      const { summary: generated } = await geminiService.summarizeChangeLogs(
        { commitMessages: loaded.map((entry) => entry.commitMessage) },
        controller.signal
      );
      if (controller.signal.aborted) {
        return;
      }
      setSummary(generated);
      if (generated.trim()) {
        onSummaryGeneratedRef.current?.(generated);
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        setError((err instanceof Error) ? err.message : 'Failed to summarize change history.');
        return;
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [avsService, geminiService, sourceBranchId, sourceBranchVersion]);

  useEffect(() => {
    loadSummary();
    return () => abortControllerRef.current?.abort();
  }, [loadSummary]);

  const tickets = useMemo(() => {
    return extractJiraTickets(entries.map((entry) => entry.commitMessage));
  }, [entries]);

  let summaryContent: JSX.Element;
  if (isLoading) {
    summaryContent = <LoadingSpinner message="Summarizing changes..." />;
  } else if (error) {
    summaryContent = <Message status={MessageStatus.error} message={error} />;
  } else if (!entries.length) {
    summaryContent = <p className={styles.empty}>No prior save messages to summarize.</p>;
  } else if (summary) {
    summaryContent = <p className={styles.summary}>{summary}</p>;
  } else {
    summaryContent = <p className={styles.empty}>No summary available.</p>;
  }

  return (
    <div className={styles.container}>
      <div>
        <h4 className={styles.heading}>Summary</h4>
        {summaryContent}
      </div>

      {(!tickets.length) ? null : (
        <div>
          <h4 className={styles.heading}>Tickets</h4>
          <ul className={styles.tickets}>
            {tickets.map((ticket) => (
              <li className={styles.ticket} key={ticket}>
                {ticket}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(!entries.length) ? null : (
        <details className={styles.commitMessages}>
          <summary className={styles.commitMessagesSummary}>
            Commit messages ({entries.length})
          </summary>
          <ul className={styles.commitMessagesList}>
            {entries.map((entry) => (
              <li
                className={styles.commitMessagesItem}
                key={entry.version}
              >
                <span className={styles.commitMessagesAuthor}>
                  {entry.author || 'Unknown'}
                </span>
                <span>: </span>
                <span className={styles.commitMessagesText}>{entry.commitMessage}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};
